import type { ServiceConfig, Session, ServiceOffering, ServiceCatalog } from './types'
import { SVC } from './catalog'

// ============================================================
// Topologia de filas + sessões e o RESOLVER de ofertas.
// Um serviço pode estar em N filas (N:N) e em sessões distintas de uma fila.
// A "oferta" = tupla resolvida (serviceId, serviceConfigId, sessionId) que a UI
// adiciona a um passo da jornada. Este módulo é o `scid-resolver`: no mock
// resolve sobre estes seeds; no RealDataSource, a mesma expansão roda sobre
// `serviceConfigs/previsions` × `serviceConfigs/{scid}/sessions`.
//
// Seeds desenham os dois eixos de ambiguidade pedidos:
//  - "Exame clínico" (sid 3) em DUAS filas (Consultório A/B, salas distintas)
//  - "Triagem" (sid 2) com DUAS sessões (Manhã/Tarde)
//  - demais serviços: 1 fila × 1 sessão → auto-vínculo (caminho feliz)
// ============================================================
export const SERVICE_CONFIGS: ServiceConfig[] = [
  { serviceConfigId: 4301, nome: 'Recepção',            sala: 'Balcão de recepção', code: 'REC', serviceIds: [1] },
  { serviceConfigId: 4302, nome: 'Triagem',             sala: 'Sala de triagem',    code: 'TRI', serviceIds: [2] },
  { serviceConfigId: 4303, nome: 'Consultório Clínico A', sala: 'Consultório 1',    code: 'CLA', serviceIds: [3] },
  { serviceConfigId: 4304, nome: 'Consultório Clínico B', sala: 'Consultório 2',    code: 'CLB', serviceIds: [3] },
  { serviceConfigId: 4305, nome: 'Audiometria',         sala: 'Cabine acústica',    code: 'AUD', serviceIds: [4] },
  { serviceConfigId: 4306, nome: 'Exame de vista',      sala: 'Sala oftalmológica', code: 'VIS', serviceIds: [5] },
  { serviceConfigId: 4307, nome: 'Coleta',              sala: 'Laboratório',        code: 'COL', serviceIds: [6] },
  { serviceConfigId: 4308, nome: 'Laudos',              sala: 'Sala de laudos',     code: 'LAU', serviceIds: [7] },
]

export const SESSIONS: Session[] = [
  { sessionId: 2139001, serviceConfigId: 4301, label: 'Dia inteiro', start: '08:00', end: '17:00' },
  { sessionId: 2139002, serviceConfigId: 4302, label: 'Manhã',       start: '08:00', end: '12:00' },
  { sessionId: 2139003, serviceConfigId: 4302, label: 'Tarde',       start: '13:00', end: '17:00' },
  { sessionId: 2139004, serviceConfigId: 4303, label: 'Dia inteiro', start: '08:00', end: '17:00' },
  { sessionId: 2139005, serviceConfigId: 4304, label: 'Dia inteiro', start: '08:00', end: '17:00' },
  { sessionId: 2139006, serviceConfigId: 4305, label: 'Dia inteiro', start: '08:00', end: '17:00' },
  { sessionId: 2139007, serviceConfigId: 4306, label: 'Dia inteiro', start: '08:00', end: '17:00' },
  { sessionId: 2139008, serviceConfigId: 4307, label: 'Dia inteiro', start: '08:00', end: '17:00' },
  { sessionId: 2139009, serviceConfigId: 4308, label: 'Dia inteiro', start: '08:00', end: '17:00' },
]

export const SCFG: Record<number, ServiceConfig> = Object.fromEntries(SERVICE_CONFIGS.map((s) => [s.serviceConfigId, s]))

// Expansão fila × sessão × serviço → ofertas concretas. `svc` é a fonte de nome/estMin
// por serviço (estático no mock; no real derivado das filas, podendo vir vazio — ver docs).
export function buildOfferings(configs: ServiceConfig[], sessions: Session[], svc: Record<number, ServiceCatalog>): ServiceOffering[] {
  const out: ServiceOffering[] = []
  for (const cfg of configs) {
    const cfgSessions = sessions.filter((s) => s.serviceConfigId === cfg.serviceConfigId)
    for (const sess of cfgSessions) {
      const sids = sess.serviceIds ?? cfg.serviceIds
      for (const serviceId of sids) {
        const s = svc[serviceId]
        out.push({
          serviceId,
          serviceName: s?.nome ?? '',
          serviceConfigId: cfg.serviceConfigId,
          filaNome: cfg.nome,
          sessionId: sess.sessionId,
          sessionLabel: sess.label,
          sessionStart: sess.start,
          sessionEnd: sess.end,
          sala: cfg.sala,
          code: cfg.code,
          estMin: s?.estMin ?? 0,
        })
      }
    }
  }
  return out
}

export const OFFERINGS: ServiceOffering[] = buildOfferings(SERVICE_CONFIGS, SESSIONS, SVC)

export const OFFERING_KEY = (o: { serviceId: number; serviceConfigId: number; sessionId: number }): string =>
  `${o.serviceId}:${o.serviceConfigId}:${o.sessionId}`

export const offeringsForService = (serviceId: number): ServiceOffering[] =>
  OFFERINGS.filter((o) => o.serviceId === serviceId)

/** sessões de uma fila (serviceConfig). No real: `serviceConfigs/{scid}/sessions`. */
export const sessionsOf = (serviceConfigId: number): Session[] =>
  SESSIONS.filter((s) => s.serviceConfigId === serviceConfigId)

export function findOffering(serviceId: number, serviceConfigId?: number, sessionId?: number): ServiceOffering | undefined {
  const all = offeringsForService(serviceId)
  if (serviceConfigId == null || sessionId == null) {return all[0]}
  return all.find((o) => o.serviceConfigId === serviceConfigId && o.sessionId === sessionId) ?? all[0]
}
